import { IS_PRIVATE } from "@/config";
import { getUploadPolicy, notifyUploadResult, uploadToCstore, uploadToPrivateCstore } from "@/services/upload";
import { ICstoreUploadRes } from "@/types/upload";
import { getFileMD5 } from "@common/utils";

/**
 * 文件选择器配置选项
 */
export interface FileSelectOptions {
  /** 允许的文件类型（如 ".docx,.pdf"） */
  accept?: string;
  /** 最大文件大小（字节） */
  maxSize?: number;
  /** 允许的文件扩展名列表 */
  allowedExtensions?: string[];
  /** 允许的 MIME 类型列表 */
  allowedMimeTypes?: string[];
  /** 文件选择成功回调 */
  onSelect: (file: File) => void;
  /** 文件验证失败回调 */
  onError?: (errorMessage: string) => void;
}

/**
 * 创建文件选择器并处理文件验证
 * 统一的文件选择逻辑，消除重复代码
 * 
 * @example
 * ```typescript
 * selectFile({
 *   accept: ".docx",
 *   maxSize: 20 * 1024 * 1024, // 20MB
 *   allowedExtensions: ["docx"],
 *   onSelect: (file) => handleFileUpload(file),
 *   onError: (error) => message.warning(error)
 * });
 * ```
 */
export const selectFile = (options: FileSelectOptions): void => {
  const {
    accept = "*",
    maxSize,
    allowedExtensions,
    allowedMimeTypes,
    onSelect,
    onError
  } = options;

  // 创建文件输入元素
  const input = document.createElement("input");
  input.type = "file";
  input.accept = accept;

  // 文件选择处理
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;

    // 验证文件
    const error = validateFile(file, { maxSize, allowedExtensions, allowedMimeTypes });
    if (error) {
      onError?.(error);
      return;
    }

    // 验证通过，触发回调
    onSelect(file);
  };

  // 触发文件选择
  input.click();
};

/**
 * 验证文件
 */
function validateFile(
  file: File,
  options: {
    maxSize?: number;
    allowedExtensions?: string[];
    allowedMimeTypes?: string[];
  }
): string | null {
  const { maxSize, allowedExtensions, allowedMimeTypes } = options;

  // 检查文件大小
  if (maxSize && file.size > maxSize) {
    const sizeMB = Math.round(maxSize / (1024 * 1024));
    return `文件大小超出 ${sizeMB}MB 限制，请调整后重新上传`;
  }

  // 检查文件扩展名
  if (allowedExtensions && allowedExtensions.length > 0) {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !allowedExtensions.includes(extension)) {
      return `仅支持上传${allowedExtensions.join("、")}格式的文件`;
    }
  }

  // 检查 MIME 类型
  if (allowedMimeTypes && allowedMimeTypes.length > 0 && file.type) {
    if (!allowedMimeTypes.includes(file.type)) {
      return "文件类型不符合要求，请选择正确的文件格式";
    }
  }

  return null;
}

// 上传文件：md5->获取策略->上传到cstore
const uploadFileToCstore = async (fileItem: File) => {
  console.log('进入uploadFileToCstore');

  try {
    const md5 = await getFileMD5(fileItem);
    console.log(`开始获取上传协议 ${fileItem.name} md5=${md5}`);
    const data = await getUploadPolicy(
      md5,
      "." + fileItem.name.split(".").pop()
    );
    let fileKey = '';
    const formData = new FormData();
    data.formFields.forEach((filed) => {
      formData.append(filed.name, filed.value);
      if (filed.name === 'key') {
        fileKey = filed.value;
      }
    });
    formData.append("file", fileItem);

    console.log(`开始上传文件 ${fileItem.name} md5=${md5}`);
    // 创建一个新的 AbortController 实例
    const controller = new AbortController();
    const { signal } = controller;
    // 私有化
    if (IS_PRIVATE) {
      await uploadToPrivateCstore(data.uploadUrl, formData, signal, () => { });
      console.log("上传完成，开始生成上传结果", fileKey);
      const res = await notifyUploadResult(fileKey);
      console.log("上传结果", res);
      return res;
    } else {
      let res: ICstoreUploadRes | undefined = undefined;
      res = await uploadToCstore(data.uploadUrl, formData, signal, () => {
        //   console.log("上传进度", progress);
      });
      console.log("文件上传成功", res);
      return res;
    }
  } catch (error) {
    console.error('文件上传失败:', error);
    throw error; // 重新抛出错误，让调用方处理
  }
};

export default uploadFileToCstore;
